import { gql } from "@apollo/client";

export enum CUSTOMER_FEATURE {
    INSPECTION = 20,
    MANAGER_PORTAL = 30,
    OWNER_PORTAL = 31,
    CONDOMINIUMOWNER_PORTAL = 32,
    TENANT_PORTAL = 33,
    EASYCONTACT = 50,
    SERVICE7000 = 51,
    YAROWA = 52,
    TICKETING = 55,
    AI_TICKET_EXTRACTION = 56,
    KREDI_FLOW = 60,
    KREDI_FLOW_PRO = 61,
    KREDI_FLOW_AI_EXTRACT = 62,
    RENTAL_EMONITOR = 70,
    ERP_MAIL = 75,
    BASEDATA_API = 80
}

export enum CUSTOMER_OPTION {
    TASKS = 100,
    MANAGEMENTS = 101,
    APPLIANCES = 102,
    MAINTENANCES = 103,
    TENANTNAME = 104,
    REALESTATEPERSONS = 105,
    CONSOLIDATIONS = 106,
    TENANT_BOOKINGS_AND_FILES = 107,
    MULTILINGUAL = 108,
    UDPATE_PERSONS_VIA_ASB = 109,
    CREDITORS = 110,
    PAYMENT_PURPOSE = 111
}

export enum CUSTOMER_FEATURE_FLAGS {
    ASSEMBLY = "FEATUREFLAG.ASSEMBLY",
    ASSEMBLY_VOTING = "FEATUREFLAG.ASSEMBLY.VOTING",
    GHOST_USER = "FEATUREFLAG.GHOST.USER",
    ASSEMBLY_FAKSIMILE_SIGNATURE = "FEATUREFLAG.ASSEMBLY.FAKSIMILE_SIGNATURE",
    MFA = "FEATUREFLAG.MFA",
    NEWS = "FEATUREFLAG.NEWS"
}

export enum USER_ROLE {}

const CUSTOMER_FRAGMENTS = gql`
    fragment customer_basic_details on ums_customers {
        id
        name
        erp
        email
        partner
        apiversion
        licencedunits
        type
        erpcloud
        subdomain
        users(where: { id: { _eq: $userid } }) {
            name1
            name2
            copy
            mfaenabled
            isSidebarCollapsed: usersettings(where: { key: { _eq: "PORTAL.IS_SIDEBAR_COLLAPSED" } }) {
                value
            }
            invoicePaneWidth: usersettings(where: { key: { _eq: "PORTAL.INVOICE_PANE_WIDTH_RATIO" } }) {
                value
            }
        }
        options(where: { option: { _in: [102, 103, 105, 107, 108, 109, 110, 111] } }) {
            option
            value
        }
        tileconfigurations: customersettings(where: { key: { _eq: "PORTAL.TILECONFIGURATION" } }) {
            value
        }
        cred_numberofvisa: customersettings(where: { key: { _eq: "CRED.NUMBEROFVISA" } }) {
            value
        }
        featureflags: customersettings(where: { key: { _like: "FEATUREFLAG%" } }) {
            key
            value
        }
    }
`;

export const GET_UMS_CUSTOMER = gql`
    ${CUSTOMER_FRAGMENTS}
    query GetUmsCustomer($userid: uuid!) {
        ums_customers {
            ...customer_basic_details
            customerfeatures {
                feature
            }
        }
        demomode: ums_customersettings(where: { key: { _eq: "TICKET.DEMOMODE" } }) {
            id
            value
        }
    }
`;

export const GET_UMS_CUSTOMER_BASIC = gql`
    ${CUSTOMER_FRAGMENTS}
    query GetUmsCustomerBasic($userid: uuid!) {
        ums_customers {
            ...customer_basic_details
        }
    }
`;

const CUSTOMER_USERS_FRAGMENT = gql`
    fragment customer_user_details on ums_users {
        customerid
        userid: id
        email
        name1
        name2
        registered
        registereddate
        activated
        emailverified
        registrationcode
        copy
        mfaenabled
        userinserted: inserted
        mfaPermission: usersettings(where: { key: { _eq: "MFA.HASPERMISSION" } }) {
            value
        }
        userroles(distinct_on: role, order_by: { role: asc }) {
            role
        }
        login_audit: audits(order_by: { timestamp: desc }, where: { event: { _in: [10, 30] } }, limit: 1) {
            timestamp
        }
        registered_audit: audits(order_by: { timestamp: desc }, where: { event: { _eq: 60 } }, limit: 1) {
            timestamp
        }
        activated_audit: audits(order_by: { timestamp: desc }, where: { event: { _eq: 70 } }, limit: 1) {
            timestamp
        }
        blocked_audit: audits(order_by: { timestamp: desc }, where: { event: { _eq: 90 } }, limit: 1) {
            timestamp
        }
    }
`;

export const GET_CUSTOMER_USERS = gql`
    ${CUSTOMER_USERS_FRAGMENT}
    query GetCustomerUsers {
        customer_users: ums_users(order_by: { name1: asc }, where: { system: { _eq: false } }) {
            ...customer_user_details
        }
    }
`;

export const GET_CUSTOMER_USER = gql`
    ${CUSTOMER_USERS_FRAGMENT}
    query GetCustomerUser($userid: uuid!) {
        customer_users: ums_users(order_by: { name1: asc }, where: { system: { _eq: false }, id: { _eq: $userid } }) {
            ...customer_user_details
        }
    }
`;

export const GET_CUSTOMER_INVOICED_USERS = gql`
    query GetCustomerInvoicedUsers($customerinvoiceid: uuid!) {
        customer_users: ums_v_customer_invoiced_users(
            order_by: { name: asc }
            where: { customerinvoiceid: { _eq: $customerinvoiceid } }
        ) {
            customerid
            userid
            email
            name
            customername
            roles
            date
        }
    }
`;

export const GET_CUSTOMER_INVOICED_RENTALS_VARIABLE = gql`
    query GetCustomerInvoicedRentalsVariable($year: numeric!, $month: numeric!) {
        rentals_variable: rent_v_rentals_variable_details(
            order_by: { rentaldate: desc }
            where: { year: { _eq: $year }, month: { _eq: $month } }
        ) {
            realestatename
            realestatenumber
            unitname
            unitnumber
            referencenumber
            tenantname
            rentaldate
            date
        }
    }
`;

export const UPSERT_CUSTOMERSETTING = gql`
    mutation UpsertCustomersetting($objects: [ums_customersettings_insert_input!]!) {
        updatecustomersetting: insert_ums_customersettings(
            objects: $objects
            on_conflict: { constraint: customer_customersetting, update_columns: value }
        ) {
            affected_rows
        }
    }
`;

export const UPSERT_USERSETTING = gql`
    mutation UpsertUsersetting($objects: [ums_usersettings_insert_input!]!) {
        updateusersetting: insert_ums_usersettings(
            objects: $objects
            on_conflict: { constraint: usersettings_customerid_userid_key, update_columns: value }
        ) {
            affected_rows
        }
    }
`;

export const GET_EASYCONTACT_INFORMATION = gql`
    query GetEasyContactInformation {
        inboxemailaddress: ums_customersettings(where: { key: { _eq: "EC.EMAILINBOX" } }) {
            id
            value
        }
        confirmationemailtext_de: ums_customersettings(where: { key: { _eq: "EC.CONFIRMATIONMAIL_DE" } }) {
            id
            value
        }
        confirmationemailtext_en: ums_customersettings(where: { key: { _eq: "EC.CONFIRMATIONMAIL_EN" } }) {
            id
            value
        }
        confirmationemailtext_fr: ums_customersettings(where: { key: { _eq: "EC.CONFIRMATIONMAIL_FR" } }) {
            id
            value
        }
        confirmationemailtext_it: ums_customersettings(where: { key: { _eq: "EC.CONFIRMATIONMAIL_IT" } }) {
            id
            value
        }
        sendemailecandticketing: ums_customersettings(where: { key: { _eq: "EC.SEND_EMAIL_IN_EC_AND_TICKETING" } }) {
            id
            value
        }
    }
`;

export const GET_KREDI_FLOW_SETTINGS = gql`
    query GetKrediflowSettings {
        visaworkflow: ums_customersettings(where: { key: { _eq: "KREDIFLOW.MANUAL_VISAWORKFLOW_DEFINITION" } }) {
            id
            value
        }
        fallbackWorkflowUser: ums_customersettings(where: { key: { _eq: "CRED.FALLBACKWORKFLOWUSER" } }) {
            id
            value
        }
        numberOfVisa: ums_customersettings(where: { key: { _eq: "CRED.NUMBEROFVISA" } }) {
            id
            value
        }
    }
`;

export const GET_KREDI_FLOW_COMMUNICATION_SETTINGS = gql`
    query GetKrediflowCommunicationSettings {
        notifyNoProcessedInvoiceToEmailAddress: ums_customersettings(
            where: { key: { _eq: "KREDIFLOW.NOTIFY_NO_PROCESSED_INVOICE_TO_EMAIL_ADDRESS" } }
        ) {
            id
            value
        }
    }
`;

export const GET_EASYCONTACT_TICKETING_COMMUNICATION_SETTINGS = gql`
    query GetEasycontactTicketingCommunicationSettings {
        sendEmailEcAndTicketing: ums_customersettings(where: { key: { _eq: "EC.SEND_EMAIL_IN_EC_AND_TICKETING" } }) {
            id
            value
        }
        ecEmailInbox: ums_customersettings(where: { key: { _eq: "EC.EMAILINBOX" } }) {
            id
            value
        }
        ecConfirmationMailDE: ums_customersettings(where: { key: { _eq: "EC.CONFIRMATIONMAIL_DE" } }) {
            id
            value
        }
        ecConfirmationMailEN: ums_customersettings(where: { key: { _eq: "EC.CONFIRMATIONMAIL_EN" } }) {
            id
            value
        }
        ecConfirmationMailFR: ums_customersettings(where: { key: { _eq: "EC.CONFIRMATIONMAIL_FR" } }) {
            id
            value
        }
        ecConfirmationMailIT: ums_customersettings(where: { key: { _eq: "EC.CONFIRMATIONMAIL_IT" } }) {
            id
            value
        }
        ticketBillingEmail: ums_customersettings(where: { key: { _eq: "TICKET.BILLINGEMAIL" } }) {
            id
            value
        }
        ticketBillingAddress: ums_customersettings(where: { key: { _eq: "TICKET.BILLINGADDRESS" } }) {
            id
            value
        }
        ticketDemoMode: ums_customersettings(where: { key: { _eq: "TICKET.DEMOMODE" } }) {
            id
            value
        }
    }
`;

export const GET_OLD_ASSEMBLY_WITH_GENERAL_DOCUMENTS = gql`
    query GetOldAssemblyWithGeneralDocuments {
        oldAssemblyWithGeneralDocuments: ums_customersettings(
            where: { key: { _eq: "ASSEMBLY.OLDASSEMBLYWITHGENERALDOCUMENTS" } }
        ) {
            id
            value
        }
    }
`;

export const GET_SERVICE_ID_BY_CUSTOMER_FEATURE = gql`
    query GetServiceIdByCustomerFeature($customerfeature: Int!) {
        service: uccm_services(where: { feature: { _eq: $customerfeature } }) {
            id
        }
    }
`;
